/**
 * Mobile remote control view, settings panel only, no portal auth.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "@jari-tools/shared";
import bodyHtml from "./emdr-body.html?raw";
import "./design-colors.css";
import "./style.css";
import "./figures.css";
import "./tool.css";
import "./racebaan.css";
import "./danspas.css";
import type { WsConnectionStatus } from "./engine/ws-client";
import {
  bootstrapRemoteEmdr,
  connectRemote,
  disconnectRemote,
  sendPlay,
  sendSud,
  sendRemoteExit,
} from "./engine/bootstrap-remote";

import { ClientStatusBanner } from "./components/ClientStatusBanner";
import type { ClientPresence } from "./lib/beeldbellen-status";

const CODE_PATTERN = /^[A-Z0-9]{6}$/;

type ViewPhase = "entry" | "session" | "host-gone" | "closed";

function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
}

function statusLabel(
  status: WsConnectionStatus,
  attempt: number,
  hostOnline: boolean
): string {
  if (status === "connected" && !hostOnline) {
    return "Groot scherm niet bereikbaar";
  }
  switch (status) {
    case "connecting":
      return "Verbinding maken…";
    case "connected":
      return "Verbonden met sessie";
    case "reconnecting":
      return `Verbinding verbroken. Probeert opnieuw (poging ${attempt})…`;
    case "closed":
      return "Verbinding gesloten";
    default:
      return "Niet verbonden";
  }
}

function StatusPill({
  status,
  attempt,
  hostOnline,
}: {
  status: WsConnectionStatus;
  attempt: number;
  hostOnline: boolean;
}) {
  if (status === "idle") return null;
  const pillStatus =
    status === "connected" && !hostOnline ? "host-offline" : status;
  return (
    <div
      className={`remote-status-pill remote-status-pill--${pillStatus} remote-status-pill--mobile`}
      role="status"
      aria-live="polite"
    >
      <span className="remote-status-dot" aria-hidden="true" />
      <span>{statusLabel(status, attempt, hostOnline)}</span>
    </div>
  );
}

function CodeEntryScreen({ onConnect }: { onConnect: (code: string) => void }) {
  const [value, setValue] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = normalizeCode(value);
    if (CODE_PATTERN.test(code)) onConnect(code);
  };

  return (
    <div className="emdr-remote-entry">
      <div className="emdr-remote-entry-card card">
        <h1>EMDR: Mobiele bediening</h1>
        <p className="emdr-remote-entry-lead">
          Scan de QR-code op het grote scherm, of voer de 6-tekens code handmatig in.
        </p>
        <form onSubmit={submit} className="emdr-remote-code-form">
          <label htmlFor="remote-code-input">Sessiecode</label>
          <input
            id="remote-code-input"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={6}
            placeholder="Bijv. A3BX9K"
            value={value}
            onChange={(e) => setValue(normalizeCode(e.target.value))}
          />
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={!CODE_PATTERN.test(normalizeCode(value))}
          >
            Verbinden
          </button>
        </form>
      </div>
    </div>
  );
}

function HostGoneScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="emdr-remote-entry">
      <div className="emdr-remote-entry-card card emdr-remote-error">
        <h1>Sessie niet gevonden</h1>
        <p>
          Deze code bestaat niet of is verlopen. Vraag op het grote scherm een nieuwe QR-code aan
          via het tabblad <strong>Bediening</strong>.
        </p>
        <button type="button" className="btn btn-primary btn-block" onClick={onRetry}>
          Andere code invoeren
        </button>
      </div>
    </div>
  );
}

function ClosedScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="emdr-remote-entry">
      <div className="emdr-remote-entry-card card">
        <h1>Sessie beëindigd</h1>
        <p>De bal is gestopt. Bij beeldbellen heeft je cliënt een melding gekregen dat de sessie is afgelopen.</p>
        <button type="button" className="btn btn-secondary btn-block" onClick={onRetry}>
          Opnieuw verbinden
        </button>
      </div>
    </div>
  );
}

const REMOTE_TITLE = "EMDR: Mobiele bediening";

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}

function useSessionTimer(playing: boolean) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const accumulatedRef = useRef(0);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!playing) {
      // Bij pauze blijft de duur van de set staan, zodat je hem kunt aflezen.
      if (startedAtRef.current !== null) {
        setElapsedMs(Date.now() - startedAtRef.current);
        startedAtRef.current = null;
      }
      return;
    }

    // Elke start is een nieuwe set en telt vanaf nul.
    accumulatedRef.current = 0;
    setElapsedMs(0);
    startedAtRef.current = Date.now();
    const interval = window.setInterval(() => {
      if (startedAtRef.current === null) return;
      setElapsedMs(accumulatedRef.current + (Date.now() - startedAtRef.current));
    }, 250);

    return () => window.clearInterval(interval);
  }, [playing]);

  const reset = useCallback(() => {
    accumulatedRef.current = 0;
    startedAtRef.current = playing ? Date.now() : null;
    setElapsedMs(0);
  }, [playing]);

  return { elapsedMs, reset };
}

function SessionTimer({ elapsedMs }: { elapsedMs: number }) {
  return (
    <div className="emdr-remote-timer">
      <span className="emdr-remote-timer-label">Set-tijd</span>
      <time
        className="emdr-remote-timer-display"
        dateTime={`PT${Math.floor(elapsedMs / 1000)}S`}
        aria-live="off"
      >
        {formatElapsed(elapsedMs)}
      </time>
    </div>
  );
}

export function RemoteApp({ initialCode }: { initialCode: string | null }) {
  useDocumentTitle(REMOTE_TITLE);
  const hostRef = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState<string | null>(() => {
    if (initialCode && CODE_PATTERN.test(normalizeCode(initialCode))) {
      return normalizeCode(initialCode);
    }
    return null;
  });
  const [phase, setPhase] = useState<ViewPhase>(code ? "session" : "entry");
  const [wsStatus, setWsStatus] = useState<WsConnectionStatus>("idle");
  const [wsAttempt, setWsAttempt] = useState(0);
  const [hostOnline, setHostOnline] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [clientPresence, setClientPresence] = useState<ClientPresence | null>(null);
  const [clientLink, setClientLink] = useState<string | null>(null);
  const [sudOpen, setSudOpen] = useState(false);
  const [audience, setAudience] = useState<"volwassenen" | "kinderen">("volwassenen");
  const { elapsedMs, reset: resetTimer } = useSessionTimer(playing);
  const controlsDisabled =
    wsStatus === "reconnecting" ||
    wsStatus === "connecting" ||
    (wsStatus === "connected" && !hostOnline);

  const updateUrl = useCallback((sessionCode: string | null) => {
    const url = new URL(location.href);
    if (sessionCode) url.searchParams.set("remote", sessionCode);
    else url.searchParams.delete("remote");
    history.replaceState(null, "", url.toString());
  }, []);

  const connectWithCode = useCallback(
    (sessionCode: string) => {
      const normalized = normalizeCode(sessionCode);
      if (!CODE_PATTERN.test(normalized)) return;
      setCode(normalized);
      setPhase("session");
      updateUrl(normalized);
    },
    [updateUrl]
  );

  const handleExit = useCallback(() => {
    const ok = window.confirm(
      "Sessie beëindigen? De bal stopt. Bij beeldbellen ziet je cliënt dat de sessie is afgelopen."
    );
    if (!ok) return;
    sendRemoteExit();
    setPhase("closed");
    setWsStatus("closed");
  }, []);

  const resetToEntry = useCallback(() => {
    disconnectRemote();
    setCode(null);
    setPhase("entry");
    setPlaying(false);
    resetTimer();
    setWsStatus("idle");
    setHostOnline(false);
    updateUrl(null);
    if (hostRef.current) {
      hostRef.current.innerHTML = "";
      delete hostRef.current.dataset.booted;
    }
  }, [updateUrl, resetTimer]);

  useEffect(() => {
    if (phase !== "session" || !code || !hostRef.current) return;

    let cancelled = false;
    const host = hostRef.current;

    if (!host.dataset.booted) {
      host.innerHTML = bodyHtml;
      host.dataset.booted = "1";
    }

    void bootstrapRemoteEmdr({ isPaid: true }).then(() => {
      if (cancelled) return;
      connectRemote(code, {
        onStatus: (status, attempt) => {
          setWsStatus(status);
          setWsAttempt(attempt);
          if (status === "connecting" || status === "reconnecting") {
            setHostOnline(false);
          }
        },
        onPlayingChange: setPlaying,
        onSudChange: setSudOpen,
        onAudienceChange: setAudience,
        onHostGone: () => setPhase("host-gone"),
        onHostOffline: () => setHostOnline(false),
        onHostOnline: () => setHostOnline(true),
        onClosed: () => setPhase("closed"),
        onClientChange: (presence, link) => {
          setClientPresence(presence);
          setClientLink(link);
        },
      });
    });

    return () => {
      cancelled = true;
      disconnectRemote();
    };
  }, [phase, code]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.classList.toggle("emdr-remote-controls-disabled", controlsDisabled);
  }, [controlsDisabled]);

  if (phase === "entry") {
    return <CodeEntryScreen onConnect={connectWithCode} />;
  }

  if (phase === "host-gone") {
    return <HostGoneScreen onRetry={resetToEntry} />;
  }

  if (phase === "closed") {
    return <ClosedScreen onRetry={resetToEntry} />;
  }

  return (
    <div className="emdr-remote-app">
      <StatusPill status={wsStatus} attempt={wsAttempt} hostOnline={hostOnline} />
      {wsStatus === "connected" && !hostOnline ? (
        <p className="emdr-remote-host-offline-hint" role="status">
          Open het tabblad <strong>Bediening</strong> op het grote scherm om opnieuw te
          verbinden. Instellingen hieronder zijn tijdelijk uitgeschakeld.
        </p>
      ) : null}

      {clientPresence && (clientPresence.count > 0 || clientPresence.everJoined) ? (
        <ClientStatusBanner
          presence={clientPresence}
          clientLink={clientLink}
          compact
          onPause={() => {
            setPlaying(false);
            sendPlay(false);
          }}
        />
      ) : null}

      <div className="emdr-remote-playbar">
        <div className="emdr-remote-playbar-actions">
          <button
            type="button"
            className={`btn ${playing ? "btn-secondary" : "btn-primary"} emdr-remote-play-btn`}
            disabled={controlsDisabled}
            aria-pressed={playing}
            onClick={() => {
              const next = !playing;
              setPlaying(next);
              sendPlay(next);
            }}
          >
            {playing ? "⏸ Pauze" : sudOpen ? "▶ Doorgaan" : "▶ Start"}
          </button>
          {audience === "kinderen" && !sudOpen ? (
            <button
              type="button"
              className="btn btn-secondary emdr-remote-play-btn"
              disabled={controlsDisabled}
              onClick={() => sendSud(true)}
            >
              Pauze met SUD
            </button>
          ) : null}
        </div>
        <SessionTimer elapsedMs={elapsedMs} />
      </div>

      <div className="emdr-remote-host jari-tool-emdr" ref={hostRef} />

      <footer className="emdr-remote-footer">
        <button
          type="button"
          className="btn emdr-remote-exit-btn"
          onClick={handleExit}
        >
          Sessie beëindigen
        </button>
      </footer>
    </div>
  );
}
